import { useState, useCallback } from "react";
import { Spinner } from "@chakra-ui/react";
import { FiExternalLink, FiDownload, FiMessageSquare } from "react-icons/fi";
import DOMPurify from "dompurify";
import { PanelSupplierScores } from "newui/features/Rfx/Evaluation/types/scoring.type";
import { useNavigationPaths } from "newui/features/Auth/hooks/useNavigationPaths";
import { generatePdf } from "components/RFQ/PdfDownloadButton/api-PdfDownload";

interface ReadOnlyScoreTableProps {
	suppliers: PanelSupplierScores[];
	rfxId: number;
	onAddClarification?: (inviteId: number) => void;
}

export const ReadOnlyScoreTable: React.FC<ReadOnlyScoreTableProps> = ({ suppliers, rfxId, onAddClarification }) => {
	const { clientPaths } = useNavigationPaths();
	const [pdfLoadingId, setPdfLoadingId] = useState<number | null>(null);

	const handlePdfDownload = useCallback((inviteId: number) => {
		setPdfLoadingId(inviteId);
		generatePdf("responses", rfxId, inviteId, false).finally(() => {
			setPdfLoadingId(null);
		});
	}, [rfxId]);

	return (
		<div className="space-y-4">
			{suppliers.map((supplier) => (
				<div
					key={supplier.invite_id}
					className="rounded-lg border border-gray-200 bg-white"
				>
					<div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
						<span className="text-sm font-medium text-gray-900">
							{supplier.supplier_name}
						</span>
						<div className="flex items-center gap-2">
							<a
								href={clientPaths.viewRfxSubmission(rfxId, supplier.invite_id)}
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
								data-testid={`view-response-${supplier.invite_id}`}
							>
								<FiExternalLink size={12} />
								View Response
							</a>
							<button
								type="button"
								onClick={() => handlePdfDownload(supplier.invite_id)}
								disabled={pdfLoadingId === supplier.invite_id}
								className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
								data-testid={`download-pdf-${supplier.invite_id}`}
							>
								{pdfLoadingId === supplier.invite_id ? <Spinner size="xs" /> : <FiDownload size={12} />}
								PDF
							</button>
							{onAddClarification && (
								<button
									type="button"
									onClick={() => onAddClarification(supplier.invite_id)}
									className="inline-flex items-center gap-1 rounded-md bg-[#0031DD] px-2.5 py-1 text-xs font-medium text-white hover:bg-[#0031DD]/90 transition-colors"
									data-testid={`add-clarification-${supplier.invite_id}`}
								>
									<FiMessageSquare size={12} />
									Add Clarification
								</button>
							)}
						</div>
					</div>
					<div className="overflow-x-auto">
						<table className="w-full table-fixed border-collapse text-sm">
							<colgroup>
								<col className="w-[320px]" />
								<col className="w-[60px]" />
								<col className="w-[70px]" />
								<col className="w-[80px]" />
								<col />
							</colgroup>
							<thead>
								<tr className="text-left text-xs text-gray-400">
									<th className="px-4 py-2 font-medium">Criterion</th>
									<th className="px-4 py-2 font-medium text-right">Weight</th>
									<th className="px-4 py-2 font-medium text-right">Score</th>
									<th className="px-4 py-2 font-medium text-right">Weighted</th>
									<th className="px-4 py-2 font-medium">Comment</th>
								</tr>
							</thead>
							<tbody>
								{supplier.criteria.map((c) => (
									<tr
										key={c.rfx_criteria_id}
										className="border-t border-gray-50 align-top"
									>
										<td className="px-4 py-2 text-gray-700 break-words">
											{c.criterion_name}
										</td>
										<td className="px-4 py-2 text-right text-gray-500 tabular-nums">
											{c.criterion_weight}%
										</td>
										<td className="px-4 py-2 text-right font-medium tabular-nums">
											{c.score !== null ? c.score : "—"}
										</td>
										<td className="px-4 py-2 text-right font-medium tabular-nums text-gray-500">
											{c.score !== null
												? (() => { const ws = c.score * (c.criterion_weight / 100); return ws % 1 === 0 ? ws : ws.toFixed(1); })()
												: "—"}
										</td>
										<td className="px-4 py-2 text-gray-500 text-xs">
											{c.comment ? (
												<div
													className="prose prose-xs max-w-none [&>p]:m-0"
													dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(c.comment) }}
												/>
											) : "—"}
										</td>
									</tr>
								))}
							</tbody>
							<tfoot>
								<tr className="border-t border-gray-200">
									<td className="px-4 py-2 text-sm font-medium text-gray-900">Total</td>
									<td className="px-4 py-2" />
									<td className="px-4 py-2" />
									<td className="px-4 py-2 text-right font-bold tabular-nums text-gray-900 text-base">
										{(() => {
											const scored = supplier.criteria.filter((c) => c.score !== null);
											if (scored.length === 0) return "—";
											const ws = scored.reduce((sum, c) => sum + (c.score ?? 0) * (c.criterion_weight / 100), 0);
											return ws % 1 === 0 ? ws : ws.toFixed(1);
										})()}
									</td>
									<td className="px-4 py-2" />
								</tr>
							</tfoot>
						</table>
					</div>
				</div>
			))}
		</div>
	);
};
